import type { FrameformerQualityTier } from './constants'

// for the rist-server
export interface AddInputStreamUnix {
    channelId: number
    streamId: number
    localPath: string
}

export enum RistServerFecMode {
    none = 0,
    '1D' = 1,
    '2D' = 2,
}

export type SdiBoardVendor = 'dektec' | 'deltacast'

// for the rist-server
export interface AddInputStreamUdp {
    channelId: number
    streamId: number
    localIp: string
    localPort: number
    multicastAddress?: string
    sourceAddresses?: string[]
    useExtendedSequenceNumber: boolean
    parseAsTs: boolean
    failoverPriority?: number
    packetFormat?: 'rtp'
}

// for the rist-server
export interface AddInputStreamRtp {
    channelId: number
    streamId: number
    localIp: string
    localPort: number
    multicastAddress?: string
    sourceAddresses?: string[]
    useFec?: boolean
}

export interface Create2110System {
    mainCpu: number
    hugePagesDir: string
    ports: Array<{
        portId: number
        pciAddress: string
        localIp: string
        flowCpuSet: number[]
        fec?: 'keep' | 'off' | 'baser' | 'rs' | 'auto'
    }>
    ptp: {
        enabled: boolean
        domain: number
        listenPorts: number[]
    }
}

export interface AddInputStream2110 {
    channelId: number
    streamId: number
    portId: number
    multicastGroup: string
    udpPort: number
    sources?: string[]
    width: number
    height: number
    framerate: [number, number]
    video: AddInputStreamSdi['video']
    muxRate?: number
    sdt?: AddInputStreamSdi['sdt']
}

// for the rist-server
export interface AddInputStreamRist {
    /** Common properties **/
    channelId: number
    streamId: number
    localIp: string
    localPort: number
    failoverPriority?: number

    /// Note: When useReducedOverhead is set to True, localIp and multicastAddress shall not be set!
    useReducedOverhead: false

    /// Set to true at ingress point when integrating with external RIST implementations
    insertOriginTime: boolean

    /** Main profile properties **/
    /// undefined for simple profile, required for main profile
    tunnelId?: number
}

export interface AddInputStreamSrt {
    srtConnectionId: number
    channelId: number
    streamId: number
    srtStreamId?: string
    packetFormat?: 'payload' | 'rtp'
}

export interface SdiAudioStreamChannelLayout {
    frontLeft?: string
    frontRight?: string
    frontCenter?: string
}

export interface SdiAudioStreamEncoding {
    codec: 'mpeg2' | 'aacLc' | 'aacHe' | 'aacHeV2'
    bitrate: number
    encoderOptions?: Record<string, string>
}

/** The ISO 639 descriptor audio_type values ristserver accepts (H.222.0 (2021) table 2-63). */
export const audioLanguageAudioTypes = [
    'undefined',
    'cleanEffects',
    'hearingImpaired',
    'visualImpairedCommentary',
    'primary',
    'native',
    'emergency',
    'primaryCommentary',
    'alternateCommentary',
] as const

export type AudioLanguageAudioType = (typeof audioLanguageAudioTypes)[number]

export interface SdiAudioStreamLanguage {
    /** ISO 639-2 alpha-3 code in lowercase ASCII. */
    code: string
    audioType?: AudioLanguageAudioType
}

/**
 * Audio word length ristserver accepts, where "auto" follows the source. Always a string, so the key
 * has a single JSON type; a bare number is rejected.
 */
export type SdiAudioWordLengthBits = 'auto' | '16' | '20' | '24'

export interface SdiAudioStream {
    pid: number
    encoding?: SdiAudioStreamEncoding
    channelLayout: SdiAudioStreamChannelLayout
    language?: SdiAudioStreamLanguage
    /**
     * Word length AES3 passthrough is packed into the transport stream with (SMPTE ST 302M
     * bits_per_sample). Ignored when `encoding` is present. Rejected by ristserver before R4.2.
     */
    aes3WordLengthBits?: SdiAudioWordLengthBits
}

export interface SdiAncConfig {
    st2038?: { pid?: number }
    scte104?: { scte35Pid?: number }
    embedCC?: boolean
}

/**
 * Size and placement of the burn-in timer overlay. An empty object means the same as `true`:
 * automatic size, automatic top-left placement. Text and backdrop are clipped at the frame edges,
 * never resized or moved to fit, and the coordinates address the full frame for interlaced video too.
 */
export interface SdiBurnInTimerConfig {
    /**
     * Scale of ristserver's built-in 5x7 pixel font, 1 - 1024; digits come out `7 * size` pixels
     * high. Zero, or unset, picks a size from the frame dimensions.
     */
    size?: number
    /** Horizontal position of the text's top-left corner in frame pixels. Defaults to the font scale. */
    x?: number
    /** Vertical position of the text's top-left corner in frame pixels. Defaults to the font scale. */
    y?: number
}

export interface AddInputStreamSdi {
    channelId: number
    streamId: number
    boardId?: number // For Deltacast: which physical board (default 0)
    boardType?: number // For Dektec: card model (e.g., 2174, 2178)
    port: number
    vendor?: SdiBoardVendor
    muxRate?: number // Transport stream target bitrate in bits/s
    // Stamp a "DEMO" watermark on the encoded output and thumbnails; set for the demo-licensed x264 path.
    demoOverlay?: boolean
    // Stamp each frame's delivery wall-clock time (UTC) on the encoded output, for reading end-to-end latency off a monitor.
    // `true` takes the automatic size and placement; an object overrides either.
    burnInTimer?: boolean | SdiBurnInTimerConfig
    video: {
        codec?: 'mpeg2' | 'h264' | 'h265'
        encoderType?: 'cpu' | 'qsv'
        format?: '420_8' | '420_10' | '422_8' | '422_10'
        gpuSelector?: string
        bitrate: number
        bufferSize: number
        gopSize: number
        maxBFrames?: number
        closedGOP?: boolean
        intraRefresh?: boolean
        // HLG on HEVC only: 'direct' (default) writes transfer_characteristics 18 in the VUI; 'dvb' writes 14 plus an
        // alternative_transfer_characteristics SEI per ETSI TS 101 154. Rejected before R4.2.
        hlgSignalling?: 'direct' | 'dvb'
        lowPower?: boolean
        encoderOptions?: Record<string, string>
        pid?: number
    }
    audio: SdiAudioStream[]
    anc?: SdiAncConfig
    sdt?: {
        serviceName?: string
        serviceProvider?: string
    }
}

export interface SdiDecodeAudioStream {
    pid: number
    channelLayout: SdiAudioStreamChannelLayout
    /**
     * Word length declared in the AES3 channel status of the embedded audio. Does not change the
     * samples, and AES3 passthrough follows its own source regardless. Ignored before R4.2.
     */
    signalledWordLengthBits?: SdiAudioWordLengthBits
}

export interface AddOutputStreamSdi {
    channelId: number
    streamId: number
    boardId?: number // For Deltacast: which physical board (default 0)
    boardType?: number // For Dektec: card model (e.g., 2154, 2174, 2178)
    port: number
    vendor?: SdiBoardVendor
    delayMs: number
    multiStreamAlignment?: boolean
    genlock?: boolean // Genlock this output to the genlock source. Deltacast only.
    videoPrefillFrames?: number
    autoAdjustAlignerLatency?: boolean
    // Adaptive mode only: upper bound (in frames) for automatic prefill growth, capping jitter-buffer
    // memory. Omitted = ristserver's internal cap (1024 frames). Must be >= videoPrefillFrames.
    autoAdjustAlignerCeiling?: number
    video: {
        decoderType?: 'cpu' | 'qsv'
        gpuSelector?: string
    }
    audio?: SdiDecodeAudioStream[]
}

// for the rist-server
export type SdiGenlockSource = 'local' | 'externalReference'

// Supported video standards for the board's local genlock oscillator.
// Required when genlockSource is "local"; not allowed otherwise.
export type SdiLocalVideoStandard =
    | '480i59.94'
    | '576i50'
    | '720p50'
    | '1080i59.94'
    | '1080p50'
    | '1080p59.94'
    | '2160p50'
    | '2160p59.94'

// for the rist-server: setSdiBoardProperties params (Deltacast only for genlockSource)
export interface SetSdiBoardProperties {
    boardId?: number // Which physical board (default 0)
    genlockSource?: SdiGenlockSource
    // Required when genlockSource is "local"; not allowed otherwise.
    localVideoStandard?: SdiLocalVideoStandard
}

// Parameters for ristserver addInputStreamAsi command
export interface AddInputStreamAsi {
    channelId: number
    streamId: number
    port: number
    boardId?: number // For Deltacast: which physical board (default 0)
    boardType?: number // For Dektec: card model (e.g., 2174, 2178)
    vendor?: SdiBoardVendor
    bts?: boolean
}

// Parameters for ristserver addOutputStreamAsi command
export interface AddOutputStreamAsi {
    channelId: number
    streamId: number
    port: number
    boardId?: number // For Deltacast: which physical board (default 0)
    boardType?: number // For Dektec: card model (e.g., 2174, 2178)
    remuxBitrate?: number // Target output bitrate in bps. When set, enables remux mode (re-timestamps PCR/PTS/DTS). When absent, transparent passthrough
    vendor?: SdiBoardVendor
    bufferDelayMs?: number
    delayMs?: number
    delayMode?: DelayMode
    bts?: boolean
    rateControlPpm?: number
}

// for the rist-server
export interface RemoveStream {
    channelId: number
    streamId: number
}

// for the rist-server
export enum Importance {
    vital = 'vital',
    nonvital = 'nonvital',
}

/** for the rist-server
 * @param {boolean=} notifyScte35 Deprecated, only kept for backwards compatibility.
 */
export interface AddOutputStreamUdp {
    channelId: number
    streamId: number
    localIp?: string
    localPort: number
    ttl?: number
    /** @DATA_FORMAT DSCP */
    dscp?: number
    remoteIp: string
    remotePort: number
    delayMs: number
    delayMode: DelayMode
    parseAsTs: boolean
    importance?: Importance

    packetFormat?: 'payload' | 'rtp'
    fecMode?: RistServerFecMode
    fecColumns?: number
    fecRows?: number

    /**
     * @deprecated Only kept for backwards compatibility. Always false or undefined.
     */
    notifyScte35?: boolean
}

// for the rist-server
export interface AddOutputStreamUnix {
    channelId: number
    streamId: number
    delayMs: number
    delayMode: DelayMode
    remotePath: string
    importance?: Importance
}

// for the rist-server
export interface AddOutputStreamRtp {
    channelId: number
    streamId: number
    localIp?: string
    localPort: number
    remoteIp: string
    remotePort: number
    ttl?: number
    /** @DATA_FORMAT DSCP */
    dscp?: number
    /**
     * @deprecated Only kept for backwards compatibility. Always false or undefined.
     */
    notifyScte35?: boolean
}

// for the rist-server
export interface AddOutputStreamRist {
    /** Common properties **/
    channelId: number
    streamId: number
    localIp?: string
    localPort: number
    remoteIp: string
    remotePort: number
    useOriginTiming: boolean
    /// Note: When useReducedOverhead is set to True, localIp and remoteIp shall not be set!
    useReducedOverhead: false

    /** Main profile properties **/
    /// undefined for simple profile, required for main profile
    tunnelId?: number
}

// for the rist-server
export interface RistServerOutputTransformAdSplice {
    type: 'ad_splice'
    params: {
        fileReadaheadMs: number
        blackout?: boolean
        scte35: {
            pids: number[] | null
        }
    }
}

// for the rist-server
export interface RistServerOutputTransformMux {
    type: 'mux'
    params: {
        /** Output bitrate in bits per second */
        bitrate?: number
    }
}

// for the rist-server
export interface RistServerOutputTransformDemux {
    type: 'demux'
    params: object // should always be {}
}

/**
 * PID mapping rule condition - specifies which PIDs to match
 * - number: Match a specific PID [32..8191)
 * - 'any_video_stream': Match any PID declared as video stream in PMT
 * - 'any_audio_stream': Match any PID declared as audio stream in PMT
 */
export type PidMappingRuleCondition = number | 'any_video_stream' | 'any_audio_stream'

/**
 * PID mapping rule action - specifies what to do with matched PIDs
 * - number: Remap to a specific PID [32..8191)
 * - 'drop': Drop all packets matching the rule
 * - {from, to}: Remap PID automatically to a given range
 */
export type PidMappingRuleAction = number | 'drop' | { from: number; to: number }

export interface PidMappingRule {
    if: PidMappingRuleCondition
    remap: PidMappingRuleAction
}

// for the rist-server
export interface RistServerOutputTransformPidMapping {
    type: 'pid_mapping'
    params: {
        /** If defined, list of program numbers to keep (dropped otherwise) */
        programsAllowList?: number[] | null
        /** Ordered list of rules for PIDs */
        pidsRules?: PidMappingRule[] | null
    }
}

export interface RistServerOutputTransformFramerateConversion {
    type: 'framerate_convert'
    params: {
        outputFramerate?: [number, number]
        maxThreads?: number
        effort?: number
        cpuCoreIndex?: number
        realtimePrio?: number
        /** @OAS_NAME FrameformerQualityTier */
        productOption?: FrameformerQualityTier
        demoOverlay?: boolean
    }
}

// for the rist-server
export interface RistServerOutputTransformDecode {
    type: 'decode'
    params: {
        video: {
            decoderType?: 'cpu' | 'qsv'
            gpuSelector?: string
        }
    }
}

// for the rist-server
export interface RistServerOutputTransformEncode {
    type: 'encode'
    params: {
        video: {
            // pid, codec and format default to the decoded input's values when omitted (ristserver from R4.2)
            pid?: number
            codec?: 'mpeg2' | 'h264' | 'h265'
            encoderType?: 'cpu' | 'qsv'
            format?: '420_8' | '420_10' | '422_8' | '422_10'
            gpuSelector?: string
            bitrate: number
            bufferSize: number
            gopSize: number
        }
        audio: SdiAudioStream[]
    }
}

// for the rist-server
export type RistServerOutputTransform =
    | RistServerOutputTransformAdSplice
    | RistServerOutputTransformMux
    | RistServerOutputTransformDemux
    | RistServerOutputTransformPidMapping
    | RistServerOutputTransformFramerateConversion
    | RistServerOutputTransformDecode
    | RistServerOutputTransformEncode

// for the rist-server
export interface AddInternalDerivedInputSink {
    channelId: number //fromChannelId
    streamId: number //fromStreamId
    delayMs: number
    delayMode: DelayMode
}

// for the rist-server
export interface RemoveInternalDerivedInput {
    channelId: number //fromChannelId
    streamId: number //fromStreamId
    toChannelId: number
    toStreamId: number
}

export interface AddInternalDerivedInputSource {
    channelId: number // toChannelId
    streamId: number // toStreamId

    // Fetched from the derived input sink config.
    fromChannelId: number // fromChannelId
    fromStreamId: number // fromStreamId
    delayMs?: number
    delayMode?: DelayMode

    transforms?: RistServerOutputTransform[]
}

export interface AddOutputStreamSrt {
    srtConnectionId: number
    channelId: number
    streamId: number
    delayMs: number
    srtStreamId?: string
    delayMode?: DelayMode
    packetFormat?: 'payload' | 'rtp'
    transforms?: RistServerOutputTransform[]
}

// for the rist-server
export interface AddTsAnalyze {
    channelId: number
    mode: 'atsc' | 'dvb'
    analyzeTool: 'stor'
    analyzeInterval: number // in seconds
}

// for the rist-server
export interface RemoveTsAnalyze {
    channelId: number
}

// for the rist-server
export enum DelayMode {
    // jitteryBasedOnRTPAndArrivalTime = -1,
    // basedOnRtpAndArrivalTime = 0,
    basedOnArrivalTime = 1,
    basedOnOriginTime = 2,
}

// for the rist-server
export interface AddChannel {
    channelId: number
    groupId?: number
    failoverPriority?: number
    description: string
    bufferDurationMs: number
    inputMultipathPolicy: RistMultipathPolicy
}

// for the rist-server
export interface SetInputMultipathParams {
    channelId: number
    multipathPolicy: RistMultipathPolicy
}

// for the rist-server
export interface SetOutputStreamMaxBitrate {
    channelId: number
    streamId: number
    bitsPerSecond: number | null
}

// for the rist-server
export enum RistMultipathPolicy {
    // Of all healthy paths, the one with the highest priority is chosen.
    // The others are sent inhibit messages to prevent them from sending RTP.
    failover = 'failover', // This is used on output appliances where all redundant outputs have redundancy mode 'failover'. Inhibit will be sent on the secondary input if the primary input is healthy.

    // No inhibit messages are sent on any input paths.
    redundancy = 'redundancy',

    // Propagate is failover with propagation of inhibit on all rist inputs
    // when all rist outputs are inhibited and no udp output is vital.
    // As soon as one rist output is resumed, propagation of inhibit stops.
    propagate = 'propagate',
}

// for the rist-server
export interface RemoveChannel {
    channelId: number
}

export interface JsonRpcResponse<T> {
    id: number
    jsonrpc: '2.0'
    method: string
    result: T
}

// for the rist-server
export interface RistPmStats {
    unavailableSeconds?: number
    severelyErroredSeconds?: number
    erroredSeconds?: number
}

// for the rist-server
export enum RistInputMultipathState {
    active = 'active',
    standby = 'standby',
}

// for the rist-server
export enum RistOutputMultipathState {
    active = 'active',
    inhibited = 'inhibited',
}

// for the backend, shared with rist-server
export interface RistInputStats extends RistPmStats {
    bytesReceived: number
    receiveBitrate?: number
    malformedRtcpPacketsReceived: number
    malformedRtcpReceivePacketrate?: number
    packetsLost: number
    lostPacketrate?: number
    longestBurstLoss?: number
    packetsReceived: number
    receivePacketrate?: number
    packetSendErrors: number
    packetsDiscarded: number
    packetsDropped: number
    interarrivalJitter?: number // Introduced in R3.18
    propagationDelayMs: number
    retransmitBytesReceived: number
    retransmitReceiveBitrate?: number
    retransmitPacketsReceived: number
    retransmitReceivePacketrate?: number
    retransmitPacketsRequested: number
    roundtripMs: number
    rtpPacketsReceived: number
    rtpBytesReceived: number
    rtpPacketsInhibited: number
    rtcpPacketsReceived: number
    rtcpReceivePacketrate?: number
    rtcpPacketsSent: number
    rtcpSendPacketrate?: number
    streamId: number
    unsupportedRtcpPacketsReceived: number
    timestampViolations: number

    multipathState?: RistInputMultipathState
}

// for the backend, shared with rist-server
export interface RistOutputStats {
    bytesSent: number
    sendBitrate?: number
    packetSendErrors: number
    malformedRtcpPacketsReceived: number
    malformedRtcpReceivePacketrate?: number
    packetsSent: number
    sendPacketrate?: number
    retransmitBytesSent: number
    retransmitSendBitrate?: number
    retransmitPacketsSent: number
    retransmitSendPacketrate?: number
    roundtripMs: number
    rtcpPacketsReceived: number
    rtcpReceivePacketrate?: number
    rtcpPacketsSent: number
    rtcpSendPacketrate?: number
    rtpBytesSent: number
    rtpPacketsInhibited: number
    rtpPacketsSent: number
    unsupportedRtcpPacketsReceived: number
    reportedPacketLossPercent: number
    rtpPacketsDroppedBecauseOfBitrateLimit: number
    streamId: number

    multipathState?: RistOutputMultipathState
}

// for the backend, shared with rist-server
export interface UdpInputStats {
    // Number of malformed *RTP* packets. Not included in `packetsReceived`.
    packetsDiscarded: number
    // Number of packets discarded because the input wasn't active.
    packetsWhileInactive: number
    bytesReceived: number
    receiveBitrate?: number
    packetsReceived: number
    receivePacketrate?: number
    packetsLost: number
    streamId: number

    status?: UdpInputStatusCode
}

// for the rist-server
export interface UnixInputStats extends UdpInputStats {}

export interface AdInsertionStats {
    activeAdBreakCount: number
    activeBlackoutCount: number
    adBreakCount: number
    blackoutCount: number
    fileReadaheadMs: number
    lastIOFileLoadingDurationMs: number
    scte35FilteredCount: number
    scte35HeartbeatCount: number
    scte35ReceivedCount: number
}

// for the backend, shared with rist-server
export interface UdpOutputStats extends RistPmStats {
    bytesSent: number
    sendBitrate?: number
    packetsLost: number
    lostPacketrate?: number
    packetsRecovered: number
    packetsSent: number
    playoutMarginMs: number
    sendPacketrate?: number
    fecBytesSent?: number //      When packetFormat == 'rtp'
    fecSendBitrate?: number //    When packetFormat == 'rtp'
    fecPacketsSent?: number //    When packetFormat == 'rtp'
    fecSendPacketrate?: number // When packetFormat == 'rtp'
    streamId: number
    /// 'status' applies only to UDP outputs with delayMode: 2 (basedOnOriginTiming)
    status?: UdpOutputStatusCode
    delayMode: string
    packetsDropped: number
    packetsDroppedBecauseOfBitrateLimit: number
    adInsertion?: AdInsertionStats
}

// for the rist-server
export interface UnixOutputStats extends UdpOutputStats {}

// for the backend, shared with rist-server
export interface RtpInputStats {
    bytesReceived: number
    receiveBitrate?: number
    packetsReceived: number
    receivePacketrate?: number

    fecBytesReceived: number
    fecReceiveBitrate?: number
    fecPacketsDiscarded: number
    fecPacketsReceived: number
    fecReceivePacketrate?: number
    packetsRecovered?: number
    packetsUnrecovered?: number
    unrecoveredPacketrate?: number

    streamId: number
    packetsDiscarded: number
    packetsDropped: number
    packetsLost: number
    lostPacketrate?: number
    timestampViolations: number
    status?: RtpInputStatusCode
    ssrc?: number
}

// for the rist-server
export interface RtpOutputStats extends Omit<
    UdpOutputStats,
    'status' | 'delayMode' | 'packetsDropped' | 'packetsRecovered'
> {}

// for the rist-server
export interface InternalInputStats extends Omit<UdpInputStats, 'status'> {
    bytesWhileInactive: number
    packetReceiveErrors: number
    timestampViolations: number
    sequenceNumberViolations?: number
    sequenceNumberOverruns?: number
    packetsDropped: number
}

// for the rist-server
export interface InternalOutputStats extends Omit<
    UdpOutputStats,
    'delayMode' | 'status' | 'unavailableSeconds' | 'severelyErroredSeconds' | 'erroredSeconds'
> {
    fecPacketsDropped: number
    socketError: number
}

// ASI input statistics from ristserver
export interface AsiInputStats {
    streamId: number
    bytesReceived: number
    receiveBitrate?: number // Calculated from bytesReceived diff
    bytesDropped: number
    droppedBitrate?: number // Calculated from bytesDropped diff
    status?: AsiInputStatusCode
}

// ASI output statistics from ristserver
interface RistAsiOutputStats {
    streamId: number
    bytesSent: number
    bytesDropped: number
    bytesDroppedDueToOverflow: number
    bytesDroppedDueToHardwareError: number
    bytesDroppedDueToLostPlayback: number
    underrunCount: number
    syncErrorCount: number
    reconfigureCount: number
}

export interface AsiOutputStats extends RistAsiOutputStats {
    sendBitrate?: number // Calculated from bytesSent diff
    droppedBitrate?: number // Calculated from bytesDropped diff

    // Status fields (from getStatus, merged by edge-data)
    status?: AsiOutputStatusCode
    adjustmentPpm?: number
    bufferCapacityBytes?: number
    bufferFillBytes?: number
    estimatedInputBitrateBps?: number
    outputBitrateBps?: number
}

// SDI input statistics from ristserver
export interface SdiInputStats {
    streamId: number
    framesReceived: number
    framesDropped: number
    framesDroppedDueToFormatChanged?: number
    framesDroppedDueToHardwareError?: number
    framesDroppedDueToSignalLoss?: number
    framesWithInjectedSilence?: number
    framesOverrun: number
    encodeOverruns: number
    pendingMuxedPackets: number
    packetsSent: number
    packetsDropped: number
    status?: SdiInputStatusCode
}

// Raw SDI output statistics from ristserver (1-1 mapping with ristserver getStatistics() response)
interface RistSdiOutputStats {
    streamId: number
    framesSent: number
    framesDropped: number
    framesDroppedDueToAligner: number
    framesDroppedDueToHardwareError: number
    framesDroppedDueToOverflow: number
    framesRepeated: number
    ancDataDroppedDueToOverflow: number
    decoderRecvVideo: number
    decoderRecvAudio: number
    decoderRecvAnc: number
    decoderRecvUnknownPes: number
    decoderRecvOther: number
    decodedVideoFrames: number
    decoderSendErrors: number
    decoderReceiveErrors: number
    decoderResets: number
    decoderTransferErrors: number
    decoderRawAudioErrors: number
    configureAttempts: number
    configureFailures: number
    alignerResets: number
}

// Per-channel outcome of the most recent configure() audio routing pass on an SDI output.
// All fields are zero before the first successful or partially-successful configure().
export interface SdiOutputAudioRouting {
    channelsRequested: number
    channelsRouted: number
    channelsDroppedUnknownPid: number
    channelsDroppedInvalidFormat: number
}

// SdiOutputStats after edge-data diff computation, merged with properties from ristserver getStatus() response
export interface SdiOutputStats extends RistSdiOutputStats {
    ntpLatencyMs?: number
    alignerBufferLevel?: number
    alignerAvDelay?: number
    alignerJitterDelay?: number
    alignerMsaDelay?: number
    status?: SdiOutputStatusCode
    lastFailedStage?: SdiOutputLastFailedStage
    audioRouting?: SdiOutputAudioRouting
}

export enum ChannelState {
    activated = 'activated',
    deactivated = 'deactivated',
}

// for the rist-server
export interface RistChannelStats {
    channelId: number
    reorderSectionMs: number
    resetCount: number
    retransmitBudget: number
    retransmitIntervalMs: number
    retransmitRoundtripMs: number
    ristInput: RistInputStats[]
    ristOutput: RistOutputStats[]
    udpInput: UdpInputStats[]
    udpOutput: UdpOutputStats[]
    unixInput: UnixInputStats[] // unixInput is not defined in R3.13
    unixOutput: UnixOutputStats[] // unixOutput is not defined in R3.13
    rtpInput: RtpInputStats[]
    rtpOutput: RtpOutputStats[]
    internalInput: InternalInputStats[]
    internalOutput: InternalOutputStats[]
    asiInput: AsiInputStats[]
    asiOutput: RistAsiOutputStats[]
    sdiInput: SdiInputStats[]
    sdiOutput: RistSdiOutputStats[]
}

// for the backend
export interface ChannelStats {
    channelId: number
    reorderSectionMs: number
    resetCount: number
    retransmitBudget: number
    retransmitIntervalMs: number
    retransmitRoundtripMs: number
    ageOfOldestHoleMs?: number
    maxReorderTimeMs?: number
    state?: ChannelState
    ristInput: RistInputStats[]
    ristOutput: RistOutputStats[]
    udpInput: UdpInputStats[]
    udpOutput: UdpOutputStats[]
    unixInput: UnixInputStats[] // unixInput is not defined in R3.13
    unixOutput: UnixOutputStats[] // unixOutput is not defined in R3.13
    rtpInput: RtpInputStats[]
    rtpOutput: RtpOutputStats[]
    internalInput?: InternalInputStats[] // internalInput is not defined in R3.24
    internalOutput?: InternalOutputStats[] // internalOutput is not defined in R3.24
    asiInput?: AsiInputStats[] // ASI input statistics
    asiOutput?: AsiOutputStats[] // ASI output statistics
    sdiInput?: SdiInputStats[] // SDI input statistics
    sdiOutput?: SdiOutputStats[] // SDI output statistics (with status merged)
}

// for the rist-server
export interface ChannelDeltaStats {
    channelId: number
    ageOfOldestHoleMs: number
    maxReorderTimeMs: number
    ristInputs: RistInputDeltaStats[]
    ristOutputs: RistOutputDeltaStats[]
    rtpInputs: RtpInputDeltaStats[]
    rtpOutputs: RtpOutputDeltaStats[]
    udpInputs: UdpInputDeltaStats[]
    udpOutputs: UdpOutputDeltaStats[]
    internalInputs?: InternalInputDeltaStats[]
    internalOutputs?: InternalOutputDeltaStats[]
}

// for the rist-server
interface RistInputDeltaStats {
    streamId: number
    longestBurstLoss: number
    maxPathDiffDelayMs: number | null
    maxReceptionLatencyMs: number
    maxRoundtripMs: number | null
    pm: RistPmStats
}

// for the rist-server
interface RistOutputDeltaStats {
    streamId: number
    maxRoundtripMs: number | null
}

// for the rist-server
interface UdpOutputDeltaStats {
    streamId: number
    shortestPlayoutMarginMs: number | null
    pm: RistPmStats
}

// for the rist-server
interface UdpInputDeltaStats {
    streamId: number
    maxReceptionLatencyMs: number
}

// for the rist-server
interface InternalInputDeltaStats {
    streamId: number
    maxReceptionLatencyMs: number
    longestBurstLoss?: number
    pm?: RistPmStats
}

// for the rist-server
interface RtpInputDeltaStats {
    streamId: number
    pm: RistPmStats
    longestBurstLoss: number
    maxReceptionLatencyMs: number
    maxPathDiffDelayMs: number | null
    maxRoundtripMs: number | null
}

// for the rist-server
interface RtpOutputDeltaStats {
    streamId: number
    maxRoundtripMs: number | null
}

// for the rist-server
interface InternalOutputDeltaStats {
    streamId: number
    shortestPlayoutMarginMs: number | null
    pm?: RistPmStats
}

// for the rist-server
export interface TunnelRxErrors {
    badAddress: number
    bufferFull: number
    cryptoFailure: number
    malformed: number
    notIpv4: number
    notUdp: number
    socketError: number
    failedUdpChecksum: number
    versionError: number
    dropped: number
}

// for the backend
interface TunnelRxWithRate {
    bytes: number
    bitrate?: number
    error: TunnelRxErrors
    packets: number
    packetRate?: number
    keepAlivePackets: number
    keepAlivePacketrate?: number
}

// for the rist-server
export interface TunnelTxErrors {
    configurationError: number
    cryptoFailure: number
    notConnected: number
    socketError: number
    dropped: number
    packetTooLarge: number
}

interface TunnelTxWithRate {
    bytes: number
    bitrate?: number
    error: TunnelTxErrors
    packets: number
    packetRate?: number
}

// for the rist-server
export interface TunnelStatsWithRate {
    rx: TunnelRxWithRate
    tunnelId: number
    tx: TunnelTxWithRate
}

// for the backend
export interface RistStatisticsResponseDiff {
    deadlinesMissed: number
    channels: ChannelStats[]
    tunnels: TunnelStatsWithRate[]
    threads: RistStatsThreadStatus
    srt?: SrtStatsDiff[] // Added in R3.25
}

// for the backend
export interface RistStatsThreadStatus {
    hardwareConcurrency: number
    maxStreamerThreads: number
    numStreamerThreads: number
    threads: ThreadStats[]
}

interface ThreadStats {
    threadId: string
    cpuPercentage?: number // Not available on all OS (Mac). See rist-server API docs.
    osThreadId?: number // Not available on all OS (Mac). See rist-server API docs.
    runqueuePercentage?: number // Not available on all OS (Mac). See rist-server API docs.
    longestWorkMs: number
    maxDelayedTimerMs: number
}

// for the rist-server
export enum RtpInputStatusCode {
    ok = 'ok',
    oddSsrc = 'odd_ssrc',
    unknown = 'unknown',
}

// for the rist-server
export enum UdpInputStatusCode {
    active = 'active',
    standby = 'standby',
}

/**
 * Output status codes returned by the ristserver for UDP outputs.
 *
 * - `playing`: Output is playing normally.
 * - `no_data`: No data is being played because no data is available.
 * - `no_origin_time`: No origin timestamps are received. No data is being played. Applies only to outputs in delayMode 2 (based on originTiming). Status may linger after input is stopped.
 * - `no_common_time_base`: Source node and playout node do not share a common clock. It is possible, but not guaranteed, that data is played, just not at the desired time. Applies only to outputs in delayMode 2 (based on originTiming). Status may linger after input is stopped.
 * - `lagging`: Data cannot be played at the intended time. It is possible, but not guaranteed, that data is played. Applies only to outputs in delayMode 2 (based on originTiming). This can indicate that the propagation delay from the source to the output server is longer than the configured delayMs for the output, or that the playing ristserver suffers from overload and cannot be scheduled to send when it should, or that the originating server and the playing server are not properly NTP synchronized.
 *
 * @OAS_DESCRIPTION Output status codes for UDP outputs. Values: playing (normal playback), no_data (no data available), no_origin_time (no origin timestamps - delayMode 2 only), no_common_time_base (clock mismatch - delayMode 2 only), lagging (playback timing issues - delayMode 2 only).
 */
// for the rist-server
export enum UdpOutputStatusCode {
    playing = 'playing',
    noData = 'no_data',
    noOriginTime = 'no_origin_time',
    noCommonTimeBase = 'no_common_time_base',
    lagging = 'lagging',
}

// for the rist-server
export enum SdiInputStatusCode {
    locked = 'locked',
    unlocked = 'unlocked',
    lockedFaultyAudio = 'lockedFaultyAudio',
}

// for the rist-server
export enum SdiOutputStatusCode {
    playing = 'playing',
    stopped = 'stopped',
    noData = 'no_data',
}

// Stage of the most recent configure() failure on an SDI output. "none" means the most recent
// configure attempt succeeded (or no configure has happened since startup).
export enum SdiOutputLastFailedStage {
    none = 'none',
    deviceOpen = 'deviceOpen',
    outputMode = 'outputMode',
    videoStandard = 'videoStandard',
    linkStandard = 'linkStandard',
    genlock = 'genlock',
    audioEmbed = 'audioEmbed',
    awaitingGenlockReference = 'awaitingGenlockReference',
    genlockFormatMismatch = 'genlockFormatMismatch',
}

// for the rist-server
export enum AsiInputStatusCode {
    locked = 'locked',
    unlocked = 'unlocked',
}

// for the rist-server
export enum AsiOutputStatusCode {
    playing = 'playing',
    probing = 'probing',
    prefilling = 'prefilling',
}

// for the rist-server
export interface RistTunnelServer {
    tunnelId: number
    secret: string
    localIp: string
    localPort: number
    authentication: 'keepAlive'
    useEphemeralSenderPorts?: boolean
}

// for the rist-server
export interface RistTunnelClient extends RistTunnelServer {
    remoteIp: string
    remotePort: number
}

// for the rist-server
export interface RemoveInputStream {
    channelId: number
    streamId: number
}

// for the rist-server
export interface RemoveOutputStream {
    channelId: number
    streamId: number
}

export interface AddThumbnailer {
    channelId: number
    remoteAddress: string
    remotePath: string
    remotePort: number
    thumbInterval: number
}

export interface RemoveThumbnailer {
    channelId: number
}

export interface Scte35SourceFilterable {
    scte35: {
        pids: number[] | null
        eventIds: number[] | null
    }
}

export interface SetOutputStreamAdScte35Source extends Scte35SourceFilterable {
    channelId: number
    streamId: number
}

interface RistServerAdTrackingEvent {
    atMs: number
    url: string
    eventType: string
}

interface RistServerAdPlaylist {
    files: {
        path: string // Absolute path
        durationMs: number
        creativeId: string
        trackingEvents: RistServerAdTrackingEvent[]
    }[]
}

interface SetOutputStreamAdPlaylistBase {
    channelId: number
    streamId: number
    playlist: RistServerAdPlaylist // play in order of array
}

export interface SetOutputStreamAdPlaylist extends SetOutputStreamAdPlaylistBase {
    uuid: string
}

export interface SetOutputStreamAdDefaultPlaylist extends SetOutputStreamAdPlaylistBase {
    programNumber: number
}
export interface SetOutputStreamAdDefaultBlackoutPlaylist extends SetOutputStreamAdPlaylistBase {
    programNumber: number
}

export interface SetOutputStreamAdSchedule {
    channelId: number
    streamId: number
    programNumber?: number
    pid?: number
    eventId?: number
    prerollMs?: number
    durationMs?: number
    emitScte35Packet?: boolean
    playlist?: RistServerAdPlaylist
    uuid?: string
}

export interface SetOutputStreamAdScheduleEnd {
    channelId: number
    streamId: number
    delayMs?: number
    eventId?: number
    emitScte35Packet?: boolean
    pid?: number
    programNumber?: number
}

export interface SetOutputStreamAdScheduleBlackout {
    channelId: number
    streamId: number
    programNumber?: number
    pid?: number
    eventId?: number
    prerollMs?: number
    durationMs?: number
    emitScte35Packet?: boolean
    blackoutStrength: number
    playlist?: RistServerAdPlaylist
    uuid?: string
}

export interface SetOutputStreamAdScheduleBlackoutEnd {
    channelId: number
    streamId: number
    blackoutStrength: number
    delayMs?: number
    eventId?: number
    emitScte35Packet?: boolean
    pid?: number
    programNumber?: number
}

/* CPU Scheduling */
export interface CpuSchedGroup {
    cpuSet: number[]
    realtimePrio?: number
}

// for the rist-server
export interface SetCpuSched {
    stream: CpuSchedGroup
    sdi: CpuSchedGroup
    tunnel: CpuSchedGroup
}

/* SRT */

// Raw SRT link stats (cumulative counters) as reported by ristserver (1-1 mapping with ristserver model)
export interface RistSrtLinkStats {
    localEndpoint: string
    remoteEndpoint: string
    recv: {
        bytes: number
        packets: number
        packetsDropped: number
        packetsLost: number
        retransmitPacketsRequested: number
    }
    sent: {
        bytes: number
        packets: number
        packetsDropped: number
        packetsLost: number
        retransmitPacketsRequested: number
    }
}

// SRT link stats after edge-data diff computation
export interface SrtLinkStats extends RistSrtLinkStats {
    receiveBitrate: number
    sendBitrate: number
    /** Live socket state merged from ristserver's status response. */
    connected?: boolean
}

export interface SrtStats {
    srtConnectionId: number
    connectionAttempts: number
    socketResets: number
    receiveErrors: number
    sendErrors: number
    packetsDropped: number
    sockets: {
        streamId: number
        links: RistSrtLinkStats[]
    }[]
}

/** SRT stats after edge-data diff computation */
export interface SrtStatsDiff extends Omit<SrtStats, 'sockets'> {
    sockets: {
        streamId: number
        links: SrtLinkStats[]
    }[]
}

type AddSrtCommon = {
    delayMs?: number
    maxMessageSize?: number
    ttl?: number
    keySizeBits?: number
    secret?: string
    /** Expected input rate in bytes/s (SRTO_INPUTBW). Zero enables libsrt's automatic sensing. */
    inputBwBps?: number
    /** Sender overhead above inputBwBps, as a percentage (SRTO_OHEADBW). */
    overheadBwPercent?: number
}

export interface AddSrtListener extends AddSrtCommon {
    srtConnectionId: number
    bonding: boolean
    localEndpoints: {
        localIp: string
        localPort: number
    }[]
}

export interface AddSrtCaller extends AddSrtCommon {
    srtConnectionId: number
    endpoints: {
        localIp?: string
        localPort?: number
        remoteIp: string
        remotePort: number
        weight?: number
    }[]
    bondingMode?: 'broadcast' | 'backup'
}

export interface AddSrtRendezvous extends AddSrtCommon {
    srtConnectionId: number
    endpoints: {
        localIp: string
        localPort: number
        remoteIp: string
        remotePort: number
    }[]
}

export interface RemoveSrtInstance {
    srtConnectionId: number
}
